import { useState, useEffect } from 'react';
import axios from 'axios';
import { LifeBuoy, Send, CheckCircle2, RotateCcw, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { API, BulkBar, BulkCheck } from './shared';

export function SupportTab({ theme }) {
  const light = theme === 'light';
  const [tickets, setTickets] = useState(null);
  const [filter, setFilter] = useState('all');
  const [openId, setOpenId] = useState(null);
  const [selectedT, setSelectedT] = useState([]);
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);

  const cardCls = light ? 'bg-white border-gray-200 shadow-sm' : 'bg-[#1a0e2e] border-white/5';
  const hintCls = light ? 'text-gray-400' : 'text-white/35';

  const load = () => axios.get(`${API}/admin/support/tickets`, { withCredentials: true }).then(r => setTickets(r.data)).catch(() => setTickets([]));
  useEffect(() => { load(); }, []);

  const list = (tickets || []).filter(t => filter === 'all' ? true : filter === 'open' ? t.status !== 'closed' : t.status === 'closed');
  const openCount = (tickets || []).filter(t => t.status !== 'closed').length;

  const setStatus = async (t, status) => {
    try {
      await axios.put(`${API}/admin/support/tickets/${t.id}/status`, { status }, { withCredentials: true });
      toast.success(status === 'closed' ? 'Demande marquée comme traitée' : 'Demande réouverte');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
  };

  const sendReply = async (t) => {
    if (!reply.trim() || busy) return;
    setBusy(true);
    try {
      await axios.post(`${API}/admin/support/tickets/${t.id}/reply`, { message: reply }, { withCredentials: true });
      toast.success('Réponse envoyée par email à l\'abonné');
      setReply('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erreur'); }
    setBusy(false);
  };

  return (
    <div data-testid="support-tab">
      <h1 className={`text-2xl font-bold mb-1 ${light ? 'text-gray-900' : 'text-white'}`}>Support</h1>
      <p className={`text-xs mb-5 ${hintCls}`}>Demandes envoyées par les abonnés depuis le Centre d'aide. Les réponses sont envoyées par email.</p>
      <div className={`inline-flex rounded-lg p-0.5 mb-5 ${light ? 'bg-gray-100' : 'bg-white/5'}`}>
        {[['all', `Toutes (${(tickets || []).length})`], ['open', `Ouvertes (${openCount})`], ['closed', `Traitées (${(tickets || []).length - openCount})`]].map(([id, lbl]) => (
          <button key={id} onClick={() => setFilter(id)} data-testid={`support-filter-${id}`}
            className={`px-4 py-1.5 text-xs font-bold rounded-md transition-colors ${filter === id ? 'bg-[#E9641A] text-white' : light ? 'text-gray-500 hover:text-gray-800' : 'text-white/40 hover:text-white'}`}>{lbl}</button>
        ))}
      </div>
      <BulkBar items={list} selected={selectedT} setSelected={setSelectedT} kind="support" light={light} onDone={() => { setSelectedT([]); load(); }} />
      <div className="space-y-2">
        {list.map(t => {
          const opened = openId === t.id;
          return (
            <div key={t.id} className={`rounded-xl border ${cardCls}`} data-testid={`support-ticket-${t.id}`}>
              <div className="w-full flex items-start gap-1 px-2">
              <div className="pt-3 pl-1"><BulkCheck id={t.id} selected={selectedT} setSelected={setSelectedT} light={light} /></div>
              <button onClick={() => { setOpenId(opened ? null : t.id); setReply(''); }} className="flex-1 flex items-center gap-3 px-2 py-3 text-left">
                <span className={`w-9 h-9 rounded-full flex items-center justify-center flex-none ${t.status === 'closed' ? 'bg-emerald-500/10' : 'bg-[#E9641A]/15'}`}>
                  <LifeBuoy size={15} className={t.status === 'closed' ? 'text-emerald-400' : 'text-[#E9641A]'} />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className={`text-sm font-semibold truncate ${light ? 'text-gray-800' : 'text-white'}`}>{t.subject}</p>
                    <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold uppercase ${t.status === 'closed' ? 'bg-emerald-500/15 text-emerald-400' : 'bg-[#E9641A]/15 text-[#E9641A]'}`}>
                      {t.status === 'closed' ? 'Traité' : 'Ouvert'}
                    </span>
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${light ? 'bg-gray-100 text-gray-500' : 'bg-white/5 text-white/45'}`}>{t.category}</span>
                    {t.source === 'cancel_survey' && (
                      <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-red-500/15 text-red-400 uppercase" data-testid={`bug-badge-${t.id}`}>Bug résiliation</span>
                    )}
                    {t.ai_status && t.source === 'cancel_survey' && (
                      <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${t.ai_status === 'solution envoyée par IA' ? 'bg-purple-500/15 text-purple-400' : 'bg-white/10 text-white/50'}`}>
                        IA : {t.ai_status}
                      </span>
                    )}
                  </div>
                  <p className={`text-[11px] ${hintCls}`}>{t.name || t.email} · {t.email} · {new Date(t.created_at).toLocaleString('fr-FR')}</p>
                </div>
                <ChevronDown size={15} className={`flex-none transition-transform ${opened ? 'rotate-180' : ''} ${hintCls}`} />
              </button>
              </div>
              {opened && (
                <div className={`px-4 pb-4 border-t ${light ? 'border-gray-100' : 'border-white/5'}`}>
                  <p className={`text-sm mt-3 whitespace-pre-wrap ${light ? 'text-gray-600' : 'text-white/70'}`}>{t.message}</p>
                  {(t.photos || []).length > 0 && (
                    <div className="flex items-center gap-2 mt-3 flex-wrap">
                      {t.photos.map((p, i) => (
                        <a key={p} href={p} target="_blank" rel="noreferrer" data-testid={`support-photo-${i}`}>
                          <img src={p} alt={`Photo ${i + 1}`} className={`w-24 h-24 object-cover rounded-lg border hover:opacity-80 transition-opacity ${light ? 'border-gray-200' : 'border-white/10'}`} />
                        </a>
                      ))}
                    </div>
                  )}
                  {t.ai_reply && (
                    <div className={`mt-3 rounded-lg border-l-2 border-purple-400 px-3 py-2 ${light ? 'bg-purple-50' : 'bg-purple-500/5'}`} data-testid={`support-ai-reply-${t.id}`}>
                      <p className="text-[10px] mb-1 text-purple-400 font-bold">Solution envoyée par email par le Support IA{t.ai_summary ? ` — ${t.ai_summary}` : ''}</p>
                      <p className={`text-xs whitespace-pre-wrap ${light ? 'text-gray-700' : 'text-white/75'}`}>{t.ai_reply}</p>
                    </div>
                  )}
                  {(t.replies || []).map((r, i) => (
                    <div key={i} className={`mt-3 rounded-lg border-l-2 border-[#E9641A] px-3 py-2 ${light ? 'bg-gray-50' : 'bg-white/[0.03]'}`}>
                      <p className={`text-[10px] mb-1 ${hintCls}`}>Réponse de {r.by} · {new Date(r.at).toLocaleString('fr-FR')}</p>
                      <p className={`text-xs whitespace-pre-wrap ${light ? 'text-gray-700' : 'text-white/75'}`}>{r.message}</p>
                    </div>
                  ))}
                  <div className="mt-4 flex flex-col gap-2">
                    <textarea rows={3} value={reply} onChange={e => setReply(e.target.value)} maxLength={3000}
                      placeholder="Écrire une réponse — elle sera envoyée par email à l'abonné…" data-testid="support-reply-input"
                      className={`w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#E9641A] resize-none ${light ? 'bg-white border border-gray-200 text-gray-800' : 'bg-white/5 border border-white/8 text-white placeholder-white/25'}`} />
                    <div className="flex items-center gap-2">
                      <button onClick={() => sendReply(t)} disabled={busy || !reply.trim()} data-testid="support-reply-send"
                        className="flex items-center gap-1.5 bg-[#E9641A] hover:bg-[#FE975E] text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors disabled:opacity-40">
                        <Send size={12} /> {busy ? 'Envoi…' : 'Envoyer la réponse'}
                      </button>
                      {t.status !== 'closed' ? (
                        <button onClick={() => setStatus(t, 'closed')} data-testid="support-mark-closed"
                          className="flex items-center gap-1.5 text-xs font-semibold text-emerald-400 hover:text-emerald-300 px-3 py-2">
                          <CheckCircle2 size={13} /> Marquer comme traité
                        </button>
                      ) : (
                        <button onClick={() => setStatus(t, 'open')} data-testid="support-reopen"
                          className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-2 ${light ? 'text-gray-500 hover:text-gray-800' : 'text-white/45 hover:text-white'}`}>
                          <RotateCcw size={13} /> Réouvrir
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        {tickets && list.length === 0 && <p className={`text-center text-sm py-10 ${hintCls}`}>Aucune demande {filter === 'open' ? 'ouverte' : filter === 'closed' ? 'traitée' : ''} pour le moment.</p>}
        {!tickets && <p className={`text-center text-sm py-10 ${hintCls}`}>Chargement…</p>}
      </div>
    </div>
  );
}
